import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  RangoTronco,
  ModificadoresTronco,
  calcularPuntuacionTronco,
  RangoCuello,
  ModificadoresCuello,
  calcularPuntuacionCuello,
  CondicionPiernas,
  ModificadoresPiernas,
  calcularPuntuacionPiernas,
  calcularPuntuacionGrupoA,
  RangoBrazo,
  ModificadoresBrazo,
  calcularPuntuacionBrazo,
  RangoAntebrazo,
  calcularPuntuacionAntebrazo,
  RangoMuneca,
  ModificadoresMuneca,
  calcularPuntuacionMuneca,
  calcularPuntuacionGrupoB,
  CargaFuerza,
  CalidadAgarre,
  calcularPuntuacionA,
  calcularPuntuacionB,
  TipoActividadReba,
  calcularPuntuacionC,
  calcularPuntuacionFinalReba,
  obtenerNivelActuacionReba,
} from '../lib/reba';
import {
  createRebaAssessment,
  fetchRebaAssessments,
  updateRebaAssessment,
  deleteRebaAssessment,
  RebaAssessmentRow,
  RebaAssessmentResultado,
} from '../lib/gestion-tecnica-reba';
import { generateRebaReportPDF } from '../lib/gestion-tecnica-reba-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';
import {
  RebaTroncoDiagram,
  RebaCuelloDiagram,
  RebaPiernasDiagram,
  RebaBrazoDiagram,
  RebaAntebrazoDiagram,
  RebaMunecaDiagram,
} from '../components/reba/RebaBodyDiagrams';

// Mismos estilos/paleta que RuidoMethodologyPage.tsx / IluminacionMethodologyPage.tsx
// (fondo oscuro, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: '#e2e8f0', fontSize: 13,
};

// Descripción + diagrama de referencia por sección (ver src/components/reba/RebaBodyDiagrams.tsx).
const fieldDescStyle: React.CSSProperties = {
  fontSize: 12.5, color: '#94a3b8', margin: '0 0 8px 0', lineHeight: 1.45,
};
const diagramBoxStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', background: '#0b1220', border: '1px solid #1e293b',
  borderRadius: 8, padding: 10, marginBottom: 12,
};

// Colores por nivel de actuación (Tabla 21 REBA): 0=gris, 1=verde, 2=ámbar, 3=naranja, 4=rojo.
const NIVEL_COLORES: Record<number, { bg: string; border: string; text: string }> = {
  0: { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.3)', text: '#64748b' },
  1: { bg: 'rgba(47,191,113,0.12)', border: 'rgba(47,191,113,0.3)', text: '#2FBF71' },
  2: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
  3: { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)', text: '#f97316' },
  4: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
};

function ResultadoIntermedio({ label, valor }: { label: string; valor: number }) {
  return (
    <div>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>{label}</span>
      <div style={{ fontSize: 20, fontWeight: 600, color: '#e2e8f0' }}>{valor}</div>
    </div>
  );
}

export default function RebaMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que RULA) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-reba');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedAssessments, setSavedAssessments] = useState<RebaAssessmentRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');
  const [lado, setLado] = useState<'izquierdo' | 'derecho'>('derecho');

  // ── Grupo A: tronco, cuello, piernas ──
  const [rangoTronco, setRangoTronco] = useState<RangoTronco>('erguido');
  const [modTronco, setModTronco] = useState<ModificadoresTronco>({
    inclinacionLateralORotacion: false,
  });

  const [rangoCuello, setRangoCuello] = useState<RangoCuello>('flexion_0_20');
  const [modCuello, setModCuello] = useState<ModificadoresCuello>({
    inclinacionLateralORotacion: false,
  });

  const [condicionPiernas, setCondicionPiernas] = useState<CondicionPiernas>('soporte_bilateral_simetrico');
  const [modPiernas, setModPiernas] = useState<ModificadoresPiernas>({
    flexionRodillas: 'ninguna',
  });

  // ── Grupo B: brazo, antebrazo, muñeca ──
  const [rangoBrazo, setRangoBrazo] = useState<RangoBrazo>('extension_20_a_flexion_20');
  const [modBrazo, setModBrazo] = useState<ModificadoresBrazo>({
    abducidoORotado: false, hombroElevado: false, puntoApoyoOFavorGravedad: false,
  });

  const [rangoAntebrazo, setRangoAntebrazo] = useState<RangoAntebrazo>('flexion_60_100');

  const [rangoMuneca, setRangoMuneca] = useState<RangoMuneca>('neutra_o_flexion_extension_menor_15');
  const [modMuneca, setModMuneca] = useState<ModificadoresMuneca>({
    torsionODesviacion: false,
  });

  // ── Carga/fuerza, agarre y actividad muscular ──
  const [cargaFuerza, setCargaFuerza] = useState<CargaFuerza>('menor_5kg');
  const [fuerzaBrusca, setFuerzaBrusca] = useState(false);
  const [calidadAgarre, setCalidadAgarre] = useState<CalidadAgarre>('bueno');

  const [tipoActividad, setTipoActividad] = useState<TipoActividadReba>({
    posturaEstaticaMasDe1Min: false, movimientosRepetitivosMasDe4PorMin: false, cambiosPosturaImportantesOInestable: false,
  });

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimaEvaluacionGuardada, setUltimaEvaluacionGuardada] = useState<RebaAssessmentRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, assessments] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchRebaAssessments(companyId),
      ]);
      setJobPositions(positions);
      setSavedAssessments(assessments);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===== Cálculo en vivo (sin botón "Calcular") =====
  const puntuacionTronco = calcularPuntuacionTronco(rangoTronco, modTronco);
  const puntuacionCuello = calcularPuntuacionCuello(rangoCuello, modCuello);
  const puntuacionPiernas = calcularPuntuacionPiernas(condicionPiernas, modPiernas);
  const grupoA = calcularPuntuacionGrupoA(puntuacionTronco, puntuacionCuello, puntuacionPiernas);
  const puntuacionA = calcularPuntuacionA(grupoA, cargaFuerza, fuerzaBrusca);

  const puntuacionBrazo = calcularPuntuacionBrazo(rangoBrazo, modBrazo);
  const puntuacionAntebrazo = calcularPuntuacionAntebrazo(rangoAntebrazo);
  const puntuacionMuneca = calcularPuntuacionMuneca(rangoMuneca, modMuneca);
  const grupoB = calcularPuntuacionGrupoB(puntuacionBrazo, puntuacionAntebrazo, puntuacionMuneca);
  const puntuacionB = calcularPuntuacionB(grupoB, calidadAgarre);

  const puntuacionC = calcularPuntuacionC(puntuacionA, puntuacionB);
  const puntuacionFinal = calcularPuntuacionFinalReba(puntuacionC, tipoActividad);
  const nivel = obtenerNivelActuacionReba(puntuacionFinal);
  const colores = NIVEL_COLORES[nivel.nivel];

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      const resultado: RebaAssessmentResultado = {
        puntuacionTronco, puntuacionCuello, puntuacionPiernas, grupoA,
        puntuacionBrazo, puntuacionAntebrazo, puntuacionMuneca, grupoB,
        puntuacionA, puntuacionB, puntuacionC, puntuacionFinal, nivelActuacion: nivel,
      };

      const payload = {
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        lado,
        inputs: {
          rangoTronco, modTronco, rangoCuello, modCuello, condicionPiernas, modPiernas,
          rangoBrazo, modBrazo, rangoAntebrazo, rangoMuneca, modMuneca,
          cargaFuerza, fuerzaBrusca, calidadAgarre, tipoActividad,
        },
        resultado,
        createdBy: profile?.id ?? null,
      };

      if (editingId) {
        await updateRebaAssessment(editingId, payload);
      } else {
        await createRebaAssessment(payload);
      }
      // createRebaAssessment() no retorna el registro insertado (mismo patrón
      // que createRulaAssessment()) — se vuelve a consultar para obtener el
      // registro recién creado con su id real, necesario para el PDF.
      const assessments = await fetchRebaAssessments(companyId);
      setSavedAssessments(assessments);
      setUltimaEvaluacionGuardada(assessments[0] ?? null);
      setGuardadoOk(true);
      setEditingId(null);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar la evaluación');
    } finally {
      setGuardando(false);
    }
  };

  function handleEditar(a: RebaAssessmentRow) {
    setEditingId(a.id);
    setDescripcion(a.descripcion);
    setLado(a.lado);
    setSelectedJobPositionId(a.job_position_id ?? '');
    const i = a.inputs;
    setRangoTronco(i.rangoTronco);
    setModTronco(i.modTronco);
    setRangoCuello(i.rangoCuello);
    setModCuello(i.modCuello);
    setCondicionPiernas(i.condicionPiernas);
    setModPiernas(i.modPiernas);
    setRangoBrazo(i.rangoBrazo);
    setModBrazo(i.modBrazo);
    setRangoAntebrazo(i.rangoAntebrazo);
    setRangoMuneca(i.rangoMuneca);
    setModMuneca(i.modMuneca);
    setCargaFuerza(i.cargaFuerza);
    setFuerzaBrusca(i.fuerzaBrusca);
    setCalidadAgarre(i.calidadAgarre);
    setTipoActividad(i.tipoActividad);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleEliminar(id: string) {
    if (!window.confirm('¿Eliminar esta evaluación? Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    await deleteRebaAssessment(id);
    const updated = await fetchRebaAssessments(companyId);
    setSavedAssessments(updated);
    setDeletingId(null);
  }

  const handleDescargarPdf = async () => {
    if (!ultimaEvaluacionGuardada) {
      setError('No hay una evaluación guardada para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateRebaReportPDF(
        ultimaEvaluacionGuardada,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-reba']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Higiene y Gestión del Riesgo Ergonómico — Método REBA</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Rapid Entire Body Assessment. Fuente: Hignett, S. &amp; McAtamney, L. (2000). Rapid entire body assessment (REBA). Applied Ergonomics, 31(2), 201-205.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setLado('izquierdo')}
          style={{
            background: lado === 'izquierdo' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Lado Izquierdo
        </button>
        <button
          onClick={() => setLado('derecho')}
          style={{
            background: lado === 'derecho' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Lado Derecho
        </button>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Operador de ensamblaje, Digitador..."
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {/* ===== GRUPO A: tronco, cuello y piernas ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Grupo A — Tronco, cuello y piernas</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Tronco</label>
          <p style={fieldDescStyle}>
            Grado de flexión o extensión del tronco respecto a la vertical, medido entre el eje del tronco y la referencia vertical fija.
          </p>
          <div style={diagramBoxStyle}>
            <RebaTroncoDiagram rango={rangoTronco} mod={modTronco} />
          </div>
          <select style={inputStyle} value={rangoTronco} onChange={e => setRangoTronco(e.target.value as RangoTronco)}>
            <option value="erguido" style={{ color: '#000' }}>Erguido (sin flexión ni extensión)</option>
            <option value="flexion_extension_0_20" style={{ color: '#000' }}>Flexión o extensión 0°-20°</option>
            <option value="flexion_20_60_o_extension_20" style={{ color: '#000' }}>Flexión 20°-60° o extensión &gt;20°</option>
            <option value="flexion_mayor_60" style={{ color: '#000' }}>Flexión &gt;60°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modTronco.inclinacionLateralORotacion}
                onChange={e => setModTronco(prev => ({ ...prev, inclinacionLateralORotacion: e.target.checked }))} />
              Inclinación lateral o rotación del tronco (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Cuello</label>
          <p style={fieldDescStyle}>
            Grado de flexión (o extensión) del cuello, medido entre el eje de la cabeza/cuello y el eje del tronco.
          </p>
          <div style={diagramBoxStyle}>
            <RebaCuelloDiagram rango={rangoCuello} mod={modCuello} />
          </div>
          <select style={inputStyle} value={rangoCuello} onChange={e => setRangoCuello(e.target.value as RangoCuello)}>
            <option value="flexion_0_20" style={{ color: '#000' }}>Flexión 0°-20°</option>
            <option value="flexion_mayor_20_o_extension" style={{ color: '#000' }}>Flexión &gt;20° o cualquier extensión</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modCuello.inclinacionLateralORotacion}
                onChange={e => setModCuello(prev => ({ ...prev, inclinacionLateralORotacion: e.target.checked }))} />
              Inclinación lateral o rotación del cuello (+1)
            </label>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Piernas</label>
          <p style={fieldDescStyle}>
            Condición de apoyo de las piernas (soporte bilateral simétrico o soporte unilateral/inestable) y grado de flexión de rodillas.
          </p>
          <div style={diagramBoxStyle}>
            <RebaPiernasDiagram condicion={condicionPiernas} mod={modPiernas} />
          </div>
          <select style={inputStyle} value={condicionPiernas} onChange={e => setCondicionPiernas(e.target.value as CondicionPiernas)}>
            <option value="soporte_bilateral_simetrico" style={{ color: '#000' }}>Soporte bilateral, de pie o sentado, caminando de forma simétrica</option>
            <option value="soporte_unilateral_o_inestable" style={{ color: '#000' }}>Soporte unilateral, ligero o inestable</option>
          </select>
          <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
            {([
              { value: 'ninguna', label: 'Sin flexión de rodillas' },
              { value: '30_60', label: 'Flexión de rodillas 30°-60°' },
              { value: 'mayor_60', label: 'Flexión de rodillas >60° (+2)' },
            ] as { value: 'ninguna' | '30_60' | 'mayor_60'; label: string }[]).map(opt => (
              <label key={opt.value} style={checkboxLabelStyle}>
                <input type="radio" name="modPiernas" checked={(modPiernas.flexionRodillas ?? 'ninguna') === opt.value}
                  onChange={() => setModPiernas(prev => ({ ...prev, flexionRodillas: opt.value }))} />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <ResultadoIntermedio label="Tronco" valor={puntuacionTronco} />
          <ResultadoIntermedio label="Cuello" valor={puntuacionCuello} />
          <ResultadoIntermedio label="Piernas" valor={puntuacionPiernas} />
        </div>
      </div>

      {/* ===== GRUPO B: miembros superiores ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Grupo B — Miembros superiores</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Brazo</label>
          <p style={fieldDescStyle}>
            Grado de flexión/extensión del brazo, medido entre el eje del brazo (hombro–codo) y el eje del tronco.
          </p>
          <div style={diagramBoxStyle}>
            <RebaBrazoDiagram rango={rangoBrazo} mod={modBrazo} />
          </div>
          <select style={inputStyle} value={rangoBrazo} onChange={e => setRangoBrazo(e.target.value as RangoBrazo)}>
            <option value="extension_20_a_flexion_20" style={{ color: '#000' }}>Extensión 20° a flexión 20°</option>
            <option value="extension_mayor_20_o_flexion_20_45" style={{ color: '#000' }}>Extensión &gt;20° o flexión 20°-45°</option>
            <option value="flexion_45_90" style={{ color: '#000' }}>Flexión 45°-90°</option>
            <option value="flexion_mayor_90" style={{ color: '#000' }}>Flexión &gt;90°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modBrazo.abducidoORotado}
                onChange={e => setModBrazo(prev => ({ ...prev, abducidoORotado: e.target.checked }))} />
              Brazo abducido o rotado (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modBrazo.hombroElevado}
                onChange={e => setModBrazo(prev => ({ ...prev, hombroElevado: e.target.checked }))} />
              Hombro elevado (+1)
            </label>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modBrazo.puntoApoyoOFavorGravedad}
                onChange={e => setModBrazo(prev => ({ ...prev, puntoApoyoOFavorGravedad: e.target.checked }))} />
              Existe punto de apoyo o el brazo está a favor de la gravedad (-1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Antebrazo</label>
          <p style={fieldDescStyle}>
            Grado de flexión del antebrazo, medido entre el eje del antebrazo (codo–muñeca) y el eje del brazo (hombro–codo).
          </p>
          <div style={diagramBoxStyle}>
            <RebaAntebrazoDiagram rango={rangoAntebrazo} />
          </div>
          <select style={inputStyle} value={rangoAntebrazo} onChange={e => setRangoAntebrazo(e.target.value as RangoAntebrazo)}>
            <option value="flexion_60_100" style={{ color: '#000' }}>Flexión 60°-100°</option>
            <option value="flexion_menor_60_o_mayor_100" style={{ color: '#000' }}>Flexión &lt;60° o &gt;100°</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Muñeca</label>
          <p style={fieldDescStyle}>
            Grado de flexión/extensión de la muñeca, medido entre el eje de la muñeca (mano) y el eje del antebrazo.
          </p>
          <div style={diagramBoxStyle}>
            <RebaMunecaDiagram rango={rangoMuneca} mod={modMuneca} />
          </div>
          <select style={inputStyle} value={rangoMuneca} onChange={e => setRangoMuneca(e.target.value as RangoMuneca)}>
            <option value="neutra_o_flexion_extension_menor_15" style={{ color: '#000' }}>Neutra o flexión/extensión &lt;15°</option>
            <option value="flexion_extension_mayor_15" style={{ color: '#000' }}>Flexión o extensión &gt;15°</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={!!modMuneca.torsionODesviacion}
                onChange={e => setModMuneca(prev => ({ ...prev, torsionODesviacion: e.target.checked }))} />
              Torsión o desviación de la muñeca (+1)
            </label>
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <ResultadoIntermedio label="Brazo" valor={puntuacionBrazo} />
          <ResultadoIntermedio label="Antebrazo" valor={puntuacionAntebrazo} />
          <ResultadoIntermedio label="Muñeca" valor={puntuacionMuneca} />
        </div>
      </div>

      {/* ===== Carga/fuerza, agarre y actividad muscular ===== */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Carga/fuerza, agarre y actividad muscular</h2>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Carga o fuerza aplicada</label>
          <select style={inputStyle} value={cargaFuerza} onChange={e => setCargaFuerza(e.target.value as CargaFuerza)}>
            <option value="menor_5kg" style={{ color: '#000' }}>Carga &lt;5kg (0)</option>
            <option value="entre_5_10kg" style={{ color: '#000' }}>Carga 5-10kg (+1)</option>
            <option value="mayor_10kg" style={{ color: '#000' }}>Carga &gt;10kg (+2)</option>
          </select>
          <div style={{ marginTop: 8 }}>
            <label style={checkboxLabelStyle}>
              <input type="checkbox" checked={fuerzaBrusca}
                onChange={e => setFuerzaBrusca(e.target.checked)} />
              La fuerza se aplica de forma brusca o repentina (+1)
            </label>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Calidad del agarre</label>
          <select style={inputStyle} value={calidadAgarre} onChange={e => setCalidadAgarre(e.target.value as CalidadAgarre)}>
            <option value="bueno" style={{ color: '#000' }}>Bueno (0)</option>
            <option value="regular" style={{ color: '#000' }}>Regular (+1)</option>
            <option value="malo" style={{ color: '#000' }}>Malo (+2)</option>
            <option value="inaceptable" style={{ color: '#000' }}>Inaceptable (+3)</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Actividad muscular</label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={!!tipoActividad.posturaEstaticaMasDe1Min}
              onChange={e => setTipoActividad(prev => ({ ...prev, posturaEstaticaMasDe1Min: e.target.checked }))} />
            Postura estática mantenida &gt;1 minuto seguido (+1)
          </label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={!!tipoActividad.movimientosRepetitivosMasDe4PorMin}
              onChange={e => setTipoActividad(prev => ({ ...prev, movimientosRepetitivosMasDe4PorMin: e.target.checked }))} />
            Movimientos repetitivos &gt;4 veces/minuto (+1)
          </label>
          <label style={checkboxLabelStyle}>
            <input type="checkbox" checked={!!tipoActividad.cambiosPosturaImportantesOInestable}
              onChange={e => setTipoActividad(prev => ({ ...prev, cambiosPosturaImportantesOInestable: e.target.checked }))} />
            Cambios de postura importantes o postura inestable (+1)
          </label>
        </div>
      </div>

      {/* ===== Resultado ===== */}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          <ResultadoIntermedio label="Puntuación Grupo A" valor={grupoA} />
          <ResultadoIntermedio label="Puntuación Grupo B" valor={grupoB} />
          <ResultadoIntermedio label="Puntuación A" valor={puntuacionA} />
          <ResultadoIntermedio label="Puntuación B" valor={puntuacionB} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <ResultadoIntermedio label="Puntuación C" valor={puntuacionC} />
        </div>

        <span style={{ color: '#94a3b8', fontSize: 12 }}>Puntuación Final REBA</span>
        <div style={{ fontSize: 34, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 }}>{puntuacionFinal}</div>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: colores.bg, border: `1px solid ${colores.border}`,
          }}
        >
          <strong style={{ color: colores.text, fontSize: 14 }}>Nivel {nivel.nivel}</strong>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>— {nivel.riesgo}: {nivel.actuacion}</span>
        </div>
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar evaluación'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Evaluación guardada correctamente</div>
      )}

      {guardadoOk && ultimaEvaluacionGuardada && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedAssessments.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Evaluaciones guardadas</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Lado</th>
                <th style={{ padding: '6px 8px' }}>Puntuación Final</th>
                <th style={{ padding: '6px 8px' }}>Nivel</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
                <th style={{ padding: '6px 8px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {savedAssessments.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{a.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{a.lado === 'izquierdo' ? 'Izquierdo' : 'Derecho'}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.puntuacionFinal}</td>
                  <td style={{ padding: '6px 8px' }}>{a.resultado.nivelActuacion.nivel}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '6px 8px', display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => generateRebaReportPDF(a, companyId, displayCompany?.name ?? 'Empresa', profile?.id ?? null)}
                      style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                    >
                      Descargar
                    </button>
                    <button
                      onClick={() => handleEditar(a)}
                      style={{ background: 'transparent', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleEliminar(a.id)}
                      disabled={deletingId === a.id}
                      style={{
                        background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 4,
                        padding: '3px 8px', fontSize: 11, cursor: deletingId === a.id ? 'not-allowed' : 'pointer',
                        opacity: deletingId === a.id ? 0.6 : 1,
                      }}
                    >
                      {deletingId === a.id ? 'Eliminando…' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
